/**
 * Schémas de l'administration — écrans C et D des maquettes.
 *
 * Importés par l'API (qui valide) et par le front (qui valide avant
 * d'envoyer). Front et back ne peuvent pas diverger : c'est le même objet.
 */
import { z } from 'zod';
import { ROLES } from '../constants/roles.js';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
/**
 * Pagination serveur — obligatoire sur toute liste, y compris quand « il n'y
 * aura que quelques lignes au début » (skill `frontend-react-afs`).
 * Le référentiel des comptes grossit à chaque agence ouverte.
 */
export const schemaPagination = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
/** Enveloppe commune à toutes les listes paginées. */
export function schemaPage(element) {
    return z.object({
        elements: z.array(element),
        page: z.number().int(),
        limite: z.number().int(),
        total: z.number().int(),
    });
}
// ─────────────────────────────────────────────────────────────────────
//  Utilisateurs (ADM-01 à ADM-03)
// ─────────────────────────────────────────────────────────────────────
/**
 * Booléen reçu en query string.
 *
 * Pas `z.coerce.boolean()` : il applique `Boolean('false')`, qui vaut `true`.
 * `?actif=false` rendait donc les comptes actifs, et `?verrouilles=false` les
 * comptes verrouillés — l'inverse de ce que le contrat OpenAPI annonçait.
 */
const booleenDeRequete = z.union([
    z.boolean(),
    z.enum(['true', 'false']).transform((valeur) => valeur === 'true'),
]);
/**
 * Les colonnes sur lesquelles la liste des comptes se trie — côté serveur,
 * parce qu'elle est paginée : trier la page affichée mentirait dès le
 * vingt-sixième compte. `statut` ordonne actif, verrouillé, désactivé.
 */
export const TRIS_UTILISATEURS = ['nom', 'role', 'statut'];
export const ORDRES_DE_TRI = ['asc', 'desc'];
export const schemaFiltresUtilisateurs = schemaPagination.extend({
    /** Recherche libre sur le nom, le prénom ou l'adresse. */
    recherche: z.string().trim().max(120).optional(),
    /** Le code du rôle DÉFINI (ADR 0006) — un natif se filtre par son code, qui est son nom de base. */
    roleCode: z.string().trim().max(60).optional(),
    siteId: z.uuid().optional(),
    /** `true` ne rend que les comptes verrouillés — la file de travail de l'admin. */
    verrouilles: booleenDeRequete.optional(),
    actif: booleenDeRequete.optional(),
    tri: z.enum(TRIS_UTILISATEURS).default('nom'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
});
/**
 * Un compte, vu par l'administration.
 *
 * Ne contient **jamais** le hachage du mot de passe ni le secret TOTP — même
 * chiffré. Une réponse d'API qui les porterait les exposerait au premier
 * `console.log` d'un développeur (skill `securite-donnees-sante`).
 */
export const schemaUtilisateurAdministre = z.object({
    id: z.uuid(),
    // Sans format : c'est une réponse. Une adresse en base qui ne passerait plus
    // la règle de création ferait tomber toute la liste des comptes.
    email: z.string(),
    nom: z.string(),
    prenom: z.string(),
    /** Le rôle de base ; `roleCode` et `roleLibelle` disent le rôle défini porté (ADR 0006). */
    role: z.enum(ROLES),
    roleCode: z.string(),
    roleLibelle: z.string(),
    siteId: z.uuid().nullable(),
    siteNom: z.string().nullable(),
    /**
     * Ce que le praticien exerce — GÉNÉRALISTE, DENTISTE… Vide pour un rôle
     * non médical. Ne donne aucun droit : dit quels box il tient et dans
     * quelle ligne de la dotation il compte.
     */
    specialites: z.array(z.string()),
    actif: z.boolean(),
    totpActive: z.boolean(),
    verrouille: z.boolean(),
    tentativesEchouees: z.number().int(),
    derniereConnexion: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const schemaPageUtilisateurs = schemaPage(schemaUtilisateurAdministre);
/** Les spécialités d'un praticien : quelques-unes, dans les mots du registre. */
const schemaSpecialites = z.array(z.string().trim().min(1).max(80)).max(10);
/**
 * Création d'un compte.
 *
 * La complexité du mot de passe est imposée ICI, à la création — et pas à la
 * connexion, où l'exiger refuserait les comptes créés avant un durcissement
 * de la règle.
 */
export const schemaCreationUtilisateur = z.object({
    email: z
        .string()
        .trim()
        .toLowerCase()
        .check(z.email('Adresse professionnelle invalide.'))
        .max(255, 'Adresse trop longue.'),
    nom: z.string().trim().min(1, 'Nom requis.').max(120),
    prenom: z.string().trim().min(1, 'Prénom requis.').max(120),
    /** Le rôle défini à porter (ADR 0006) ; sa base fixe le périmètre et le second facteur. */
    roleCode: z.string().trim().min(1).max(60),
    /**
     * `null` pour un rôle transverse. La cohérence rôle ↔ site est vérifiée
     * côté serveur : un rôle non transverse sans site donne un périmètre vide,
     * donc un compte qui ne voit rien (skill `regles-metier-afs`).
     */
    siteId: z.uuid().nullable(),
    /** Pour un rôle médical ; absente, généraliste. Ignorée pour les autres rôles. */
    specialites: schemaSpecialites.optional(),
    motDePasse: z
        .string()
        .min(12, 'Douze caractères au minimum.')
        .max(200, 'Mot de passe trop long.')
        .regex(/[a-z]/, 'Au moins une minuscule.')
        .regex(/[A-Z]/, 'Au moins une majuscule.')
        .regex(/\d/, 'Au moins un chiffre.'),
});
/**
 * Un nouveau mot de passe provisoire pour un compte existant : le titulaire
 * l'a oublié et ne reçoit pas le courriel de réinitialisation. Mêmes règles
 * qu'à la création ; il devra le remplacer à sa prochaine connexion.
 */
export const schemaMotDePasseProvisoire = schemaCreationUtilisateur
    .pick({ motDePasse: true })
    .strict();
/** Modification d'un compte. Tout est optionnel : on ne renvoie que ce qui change. */
export const schemaModificationUtilisateur = z
    .object({
    nom: z.string().trim().min(1).max(120),
    prenom: z.string().trim().min(1).max(120),
    roleCode: z.string().trim().min(1).max(60),
    siteId: z.uuid().nullable(),
    specialites: schemaSpecialites,
    actif: z.boolean(),
})
    .partial()
    .refine((valeur) => Object.keys(valeur).length > 0, {
    message: 'Aucune modification demandée.',
});
// ─────────────────────────────────────────────────────────────────────
//  Journal des connexions et d'audit (ADM-04)
// ─────────────────────────────────────────────────────────────────────
/**
 * Les colonnes par lesquelles le journal se trie. `evenement` suit l'ordre
 * alphabétique des libellés, pas celui des codes techniques.
 */
export const TRIS_AUDIT = ['date', 'utilisateur', 'evenement', 'personne', 'entreprise'];
export const schemaFiltresAudit = schemaPagination.extend({
    /** Préfixe d'action : `auth.` pour ne voir que les connexions. */
    action: z.string().trim().max(80).optional(),
    acteurId: z.uuid().optional(),
    entiteType: z.string().trim().max(60).optional(),
    /** Bornes incluses, au format ISO. */
    depuis: z.iso.datetime().optional(),
    jusqua: z.iso.datetime().optional(),
    /**
     * Masquer les événements système (worker, migration) : ceux qui n'ont ni
     * acteur authentifié ni adresse IP. Les échecs de connexion (anonymes mais
     * avec IP) restent visibles — ce sont des événements de sécurité.
     */
    exclureSysteme: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /**
     * Recherche libre, casse et accents ignorés : l'utilisateur qui a agi, le
     * compte visé, la personne concernée (nom ou matricule), l'entreprise
     * concernée (raison sociale ou code), ou le libellé de l'événement.
     * Chaque mot doit apparaître, dans n'importe quel ordre. Deux caractères au
     * minimum : une seule lettre rendrait presque tout le journal.
     */
    recherche: z.string().trim().min(2, 'Deux caractères au minimum.').max(120).optional(),
    tri: z.enum(TRIS_AUDIT).default('date'),
    ordre: z.enum(ORDRES_DE_TRI).default('desc'),
});
/**
 * Une entrée du journal.
 *
 * `metadata` dit QUELS champs ont changé, jamais leurs valeurs dès qu'il
 * s'agit de données médicales ou nominatives (CCT § 5.2) : le contenu d'un
 * dossier ne sort jamais par le journal. Une seule exception : la correction
 * du nom ou du prénom garde l'identité avant/après (`metadata.identite`,
 * réunion du 15/10, décision du TL du 16/10).
 *
 * La personne touchée, elle, est nommée — pour TOUS les événements, accès
 * au dossier médical compris (décision du Tech Lead, 15/10). Le journal sert
 * à retrouver qui a touché à quel dossier ; il ne se lit qu'avec
 * `audit:read`. Le nom est relu à la consultation, jamais recopié dans
 * `audit_log`.
 */
export const schemaEntreeAudit = z.object({
    id: z.uuid(),
    action: z.string(),
    entiteType: z.string(),
    entiteId: z.uuid().nullable(),
    acteurId: z.uuid().nullable(),
    acteurRole: z.enum(ROLES).nullable(),
    /** Reconstitué par jointure — le journal ne duplique pas les identités. */
    acteurNom: z.string().nullable(),
    /**
     * Le compte VISÉ par l'action, quand l'entité est un compte : un
     * verrouillage n'a pas d'acteur authentifié, mais il a un compte, et
     * c'est lui qu'on cherche dans le journal.
     */
    entiteNom: z.string().nullable(),
    /**
     * La personne touchée par l'action — salarié, ayant droit ou client
     * externe —, et l'entreprise touchée. Le journal n'en garde que les
     * identifiants : les noms se relisent à la consultation, comme ceux des
     * acteurs.
     */
    personne: z
        .object({
        id: z.uuid(),
        nom: z.string(),
        matricule: z.string().nullable(),
        typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    })
        .nullable(),
    entreprise: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }).nullable(),
    siteId: z.uuid().nullable(),
    /** Le nom du centre du geste : où il a eu lieu (ADR 0002), lu à l'écran du journal. */
    siteNom: z.string().nullable(),
    adresseIp: z.string().nullable(),
    survenuLe: z.iso.datetime(),
    metadata: z.unknown().nullable(),
});
export const schemaPageAudit = schemaPage(schemaEntreeAudit);
/** Compteurs de tête de l'écran D — une requête, pas quatre. */
export const schemaResumeAudit = z.object({
    connexionsAujourdhui: z.number().int(),
    echecsAujourdhui: z.number().int(),
    comptesVerrouilles: z.number().int(),
    accesDossierMedicalAujourdhui: z.number().int(),
});
// ─────────────────────────────────────────────────────────────────────
//  Référentiel des sites
// ─────────────────────────────────────────────────────────────────────
/**
 * Une zone géographique — ADR 0003.
 *
 * Elle regroupe les sites d'une même ville d'implantation, et c'est elle qui
 * vaut périmètre d'accès pour un compte rattaché. Une zone n'est pas un
 * site : aucun acte ne s'y rattache directement.
 */
export const schemaZone = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    actif: z.boolean(),
});
/**
 * Un site, tel que les écrans le manipulent.
 *
 * `zoneId` décide du périmètre d'un compte rattaché : il couvre tous les
 * sites de la zone (ADR 0003). Une antenne ne se rattache à aucune agence :
 * l'approvisionnement part de PRISME pour tout le monde, et une antenne
 * dépend de l'entreprise qui l'héberge (ADR 0001, révision du 11/09/2026).
 */
export const schemaSite = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    type: z.enum(['AGENCE', 'ANTENNE']),
    zoneId: z.uuid(),
    actif: z.boolean(),
    /**
     * Box en service. Rendu par la liste des sites : une agence sans box n'a
     * pas de file d'attente, et cela doit se voir avant la recette, pas le jour
     * où le premier médecin cherche le sien.
     */
    nbBox: z.number().int().nonnegative().optional(),
    /**
     * Parmi eux, ceux qui attendent leur numéro de porte : ils restent hors
     * file tant que la Direction ne l'a pas donné. Rendu avec `nbBox`.
     */
    nbBoxSansPorte: z.number().int().nonnegative().optional(),
    /**
     * Comptes « Chef de centre » rattachés à ce site et chefs d'aucun centre :
     * ils n'ouvrent plus rien (décision 449, D15). Rendu par la liste des
     * sites, pour que la Direction les voie sans les chercher.
     */
    chefsSansCentre: z.number().int().nonnegative().optional(),
});
// ─────────────────────────────────────────────────────────────────────
//  Création et modification des sites et des zones — ADR 0001
// ─────────────────────────────────────────────────────────────────────
const schemaNomSite = z.string().trim().min(1, 'Nom requis.').max(120, 'Nom trop long.');
/** Un métier par ligne, et une ligne par spécialité de médecin : cinquante, c'est déjà beaucoup. */
const LIGNES_DE_DOTATION_MAX = 50;
/** Une ligne de dotation : un métier, un nombre de postes prévus. */
export const schemaEffectifDeclare = z.object({
    role: z.enum(ROLES),
    /**
     * Pour un médecin, la prestation qu'il assure : GÉNÉRALISTE, DENTISTE,
     * CARDIOLOGIE… Une ligne par spécialité, et un box par médecin prévu.
     * Absente pour un médecin : généraliste. Vide pour les autres métiers.
     */
    prestation: z.string().trim().max(80).nullable().optional(),
    /** Borné : cinq cents postes sur un site, c'est une faute de frappe. */
    effectifDeclare: z.number().int().min(0).max(500),
});
/**
 * Création d'un site. La cohérence de la dotation — rôle transverse, rôle
 * déclaré deux fois, effectif négatif — est jugée par `validerSite()` de
 * `@afs/shared`, la même règle que l'écran applique.
 *
 * La **référence** n'y figure pas : elle se dérive du nom et du type, et le
 * serveur la dédoublonne sans rien demander. Un code saisi est un code avec
 * une faute de frappe, et il identifierait ce site pour toujours
 * (skill `ux-ecrans-afs`).
 */
export const schemaCreationSite = z.object({
    nom: schemaNomSite,
    type: z.enum(['AGENCE', 'ANTENNE']),
    zoneId: z.uuid(),
    adresse: z.string().trim().max(300, 'Adresse trop longue.').nullable(),
    effectifs: z.array(schemaEffectifDeclare).max(LIGNES_DE_DOTATION_MAX),
});
/**
 * Modification d'un site. Tout est optionnel : on n'envoie que ce qui change.
 *
 * `actif: false` FERME le site — il n'est jamais supprimé : les passages, les
 * dispensations et les encaissements qui s'y sont produits portent son
 * `siteId` et restent des pièces comptables et médicales (ADR 0001).
 *
 * `effectifs`, quand il est présent, REMPLACE la dotation entière : une
 * ligne absente signifie que le site n'héberge plus ce métier.
 */
export const schemaModificationSite = z
    .object({
    nom: schemaNomSite,
    adresse: z.string().trim().max(300).nullable(),
    zoneId: z.uuid(),
    actif: z.boolean(),
    effectifs: z.array(schemaEffectifDeclare).max(LIGNES_DE_DOTATION_MAX),
})
    .partial()
    .refine((valeur) => Object.keys(valeur).length > 0, {
    message: 'Aucune modification demandée.',
});
/** Une ligne de dotation lue : prévu ET en poste, côte à côte. */
export const schemaEffectifSite = schemaEffectifDeclare.extend({
    /** Vide pour un métier sans spécialité ; toujours rempli pour un médecin. */
    prestation: z.string(),
    /**
     * Calculé depuis les comptes actifs de ce rôle sur ce site — jamais saisi.
     * Pour un médecin, les comptes qui exercent cette spécialité : un praticien
     * à deux spécialités compte dans les deux lignes.
     */
    effectifReel: z.number().int(),
});
/** Un site en détail — l'écran de gestion, avec sa dotation. */
export const schemaSiteDetaille = schemaSite.extend({
    adresse: z.string().nullable(),
    zoneNom: z.string(),
    effectifs: z.array(schemaEffectifSite),
    /**
     * Le chef du centre (`Site.responsableId`, décision 449), ou personne. Son
     * rôle de base dit à l'écran s'il se nomme « Dr » (MEDECIN) ou non.
     */
    chefDeCentre: z
        .object({ id: z.uuid(), nom: z.string(), prenom: z.string(), role: z.enum(ROLES) })
        .nullable(),
});
/**
 * Désigner le chef d'un centre, ou le retirer (`null`). Le compte se choisit
 * dans la liste ; le serveur juge le reste (RG-SEC-CENTRE).
 */
export const schemaDesignationChefDeCentre = z.strictObject({
    utilisateurId: z.uuid().nullable(),
});
/** Ouvrir une ville : plus rare qu'ouvrir une antenne, et distinct (ADR 0003). */
export const schemaCreationZone = z.object({ nom: schemaNomSite });
export const schemaModificationZone = z
    .object({ nom: schemaNomSite, actif: z.boolean() })
    .partial()
    .refine((valeur) => Object.keys(valeur).length > 0, {
    message: 'Aucune modification demandée.',
});
//# sourceMappingURL=administration.js.map